/* Dino Spirit personalized canvas: photo -> AI preview -> size -> cart.
   Mounts on every [data-canvas-customizer] element:
     data-api      Canvas Studio base URL, e.g. https://api.dinospirit.com/canvas
     data-product  id of a <script type="application/json"> with {store, template, title, aspect, gallery_aspect,
                   images: [{src, alt, thumb}], sample, terms_url, eta: {min, max} (business days),
                   variants: [{id, title, price (cents), compare (cents, struck through when above price), label}]}
                   aspect = the artwork's shape; gallery_aspect = the photo frame's (default: aspect). When they
                   differ, the customer's design is shown whole inside the frame instead of cropped.
     data-mode     "demo" shows what would go to the cart instead of calling /cart/add.js; "ready" is a ready-made
                   canvas: no photo or preview steps, the chosen size goes to the cart as it is
   Optional children the page renders inside the element: [data-dcc-rating] goes right under the title,
   [data-dcc-intro] under the price, [data-dcc-after] under the add-to-cart button; [data-dcc-eta] inside them gets the delivery dates and
   [data-dcc-preview-note] (e.g. "you see your portrait before you pay") hides while ordering without a preview.
   Plain script on purpose (no modules, no dynamic import): Shopify minifies theme assets. */
(function () {
  'use strict';

  var DEVICE_KEY = 'ds-canvas-device';
  var UPLOAD_KEY = 'ds-canvas-upload';  // last upload id, so "Try another version" works after a reload
  var UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
  var VERSIONS = ['a', 'b', 'c'];
  var POLL_MS = 1500;
  var TIMEOUT_MS = 120000;
  var MESSAGES = {
    bad_type: 'Please choose a JPG or PNG photo.',
    too_large: 'This photo is too large. Please choose one under 10 MB.',
    too_many_pixels: 'This photo is too large. Please choose a smaller one.',
    too_small: 'This photo is too small. Please choose a larger, sharper one.',
    upload_limit: 'Too many uploads today. Please try again tomorrow.',
    ip_limit: 'Daily preview limit reached. Please try again tomorrow.',
    device_limit: 'You have used all previews for now. Please try again later.',
    busy: 'We are very busy right now. Please try again in a few minutes.',
    paused: 'Previews are paused for a moment. Please try again soon.',
    disk_full: 'Previews are paused for a moment. Please try again soon.',
    blocked: 'We could not use this photo. Please try a different one.',
    generation_failed: 'Something went wrong. Please try again - this try was not counted.',
    source_missing: 'Please choose your photo again.',
    timeout: 'This is taking longer than usual. Please try again.',
    network: 'Connection problem. Please check your internet and try again.',
    bad_email: 'Please enter a valid email address.',
    unlock_limit: 'Too many attempts today. Please try again tomorrow.',
    no_photo: 'Please choose a photo first.',
    no_design: 'Create your preview first, then add it to your cart.',
    cart: 'Could not add to cart. Please try again.'
  };
  // No more previews for this customer: ordering stays open. The line carries the photo and the template
  // (_upload_id, _template) and the team paints it after the order (Canvas Studio /admin/uploads/{id}/design).
  var LATER = {
    note: 'No more free previews right now. You can still order: we\'ll paint your canvas from your photo and ' +
          'check it before printing.',
    line: 'Painted from your photo after you order'
  };

  function uuid4() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    var b = new Uint8Array(16);
    crypto.getRandomValues(b);
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    var h = Array.prototype.map.call(b, function (x) { return (x + 0x100).toString(16).slice(1); }).join('');
    return [h.slice(0, 8), h.slice(8, 12), h.slice(12, 16), h.slice(16, 20), h.slice(20)].join('-');
  }

  function remember(key, value) {
    try {
      if (value) localStorage.setItem(key, value);
      else localStorage.removeItem(key);
    } catch (e) { /* storage blocked */ }
  }

  function recall(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  }

  function deviceId() {
    var id = recall(DEVICE_KEY);
    if (!id || !UUID_RE.test(id)) {
      id = uuid4();
      remember(DEVICE_KEY, id);
    }
    return id;
  }

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    Object.keys(attrs || {}).forEach(function (k) {
      var v = attrs[k];
      if (v === null || v === undefined || v === false) return;
      if (k === 'text') node.textContent = v;
      else if (k === 'class') node.className = v;
      else if (k.slice(0, 2) === 'on') node.addEventListener(k.slice(2), v);
      else node.setAttribute(k, v === true ? '' : v);
    });
    (children || []).forEach(function (c) {
      if (c) node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return node;
  }

  function money(cents) {
    return '$' + (Number(cents || 0) / 100).toFixed(2);
  }

  function addBusinessDays(from, n) {
    var d = new Date(from.getTime());
    while (n > 0) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0 && d.getDay() !== 6) n -= 1;
    }
    return d;
  }

  function etaText(eta) {
    var now = new Date();
    var fmt = function (d) { return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); };
    return fmt(addBusinessDays(now, eta.min)) + ' - ' + fmt(addBusinessDays(now, eta.max));
  }

  function fail(code) {
    var err = new Error(code || 'generation_failed');
    err.code = code || 'generation_failed';
    return err;
  }

  function request(url, opts) {
    return fetch(url, opts).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (body) {
        body._status = r.status;
        return body;
      });
    }, function () { throw fail('network'); });
  }

  // Photo -> JPEG of at most maxSide px. Browsers apply the EXIF rotation when drawing the image.
  function shrink(file, maxSide) {
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        var s = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
        var c = document.createElement('canvas');
        c.width = Math.round(img.naturalWidth * s);
        c.height = Math.round(img.naturalHeight * s);
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        URL.revokeObjectURL(url);
        c.toBlob(function (blob) { blob ? resolve(blob) : reject(fail('bad_type')); }, 'image/jpeg', 0.9);
      };
      img.onerror = function () { URL.revokeObjectURL(url); reject(fail('bad_type')); };
      img.src = url;
    });
  }

  // ---- Face check (studio/static/face-gate.js, a module served by Canvas Studio) ----
  var GATE_VERSION = '2';
  var GATE_WAIT_MS = 12000;   // slower than this (bad network): carry on without the check, as before
  var SMALL_FACE = 150;       // px in the photo; smaller faces give a weaker likeness
  var SMALL_PHOTO = 600;      // px, short side, when no face was found
  var NOTES = {
    checking: 'Checking your photo...',
    pick: 'We found {n} faces. Tap your child\'s face.',
    no_face: 'We could not find a face in this photo. Please choose one where your child\'s face is clearly visible.',
    small: 'This photo is quite small, so the portrait may look less like your child. A larger, sharper photo works best.'
  };
  var gateLoad = null;

  function faceGate(api) {
    if (!gateLoad) {
      gateLoad = new Promise(function (resolve, reject) {
        if (window.dsFaceGate) return resolve(window.dsFaceGate);
        window.addEventListener('ds-face-gate', function () { resolve(window.dsFaceGate); }, { once: true });
        var script = document.createElement('script');
        script.type = 'module';
        script.src = api + '/static/face-gate.js?v=' + GATE_VERSION;
        script.onerror = function () { reject(fail('gate')); };
        document.head.appendChild(script);
      }).then(function (gate) { return gate.ready.then(function () { return gate; }); });
      gateLoad.catch(function () { gateLoad = null; });  // the next photo tries again
    }
    return gateLoad;
  }

  function within(promise, ms) {
    return new Promise(function (resolve, reject) {
      var timer = setTimeout(function () { reject(fail('timeout')); }, ms);
      promise.then(function (v) { clearTimeout(timer); resolve(v); }, function (e) { clearTimeout(timer); reject(e); });
    });
  }

  function loadImage(file) {
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () { resolve({ img: img, url: url }); };
      img.onerror = function () { URL.revokeObjectURL(url); reject(fail('bad_type')); };
      img.src = url;
    });
  }

  // Faces in the photo, in its own pixels (found on a copy of at most 1024 px).
  function detectFaces(gate, img) {
    var scale = Math.min(1, 1024 / Math.max(img.naturalWidth, img.naturalHeight));
    var c = document.createElement('canvas');
    c.width = Math.round(img.naturalWidth * scale);
    c.height = Math.round(img.naturalHeight * scale);
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
    return gate.faces(c).then(function (list) {
      return list.map(function (f) { return { x: f.x / scale, y: f.y / scale, w: f.w / scale, h: f.h / scale }; });
    });
  }

  // Square around a face with hair and shoulders: wide for a single face so long hair stays in, tighter
  // when there are several faces so the others stay out.
  function faceCrop(face, imgW, imgH, several) {
    var f = Math.max(face.w, face.h);
    var side = Math.min(f * (several ? 2.6 : 3.2), imgW, imgH);
    var cx = face.x + face.w / 2, cy = face.y + face.h / 2 + f * (several ? 0.25 : 0.35);
    return {
      x: Math.round(Math.min(Math.max(cx - side / 2, 0), imgW - side)),
      y: Math.round(Math.min(Math.max(cy - side / 2, 0), imgH - side)),
      side: Math.round(side)
    };
  }

  function cropToBlob(img, box) {
    var out = Math.max(512, Math.min(2048, box.side));  // the server needs 300 px or more
    var c = document.createElement('canvas');
    c.width = c.height = out;
    c.getContext('2d').drawImage(img, box.x, box.y, box.side, box.side, 0, 0, out, out);
    return new Promise(function (resolve, reject) {
      c.toBlob(function (blob) { blob ? resolve(blob) : reject(fail('bad_type')); }, 'image/jpeg', 0.92);
    });
  }

  function Customizer(root) {
    var data = document.getElementById(root.getAttribute('data-product'));
    this.root = root;
    this.api = (root.getAttribute('data-api') || '').replace(/\/+$/, '');
    this.demo = root.getAttribute('data-mode') === 'demo';
    this.ready = root.getAttribute('data-mode') === 'ready';
    this.product = JSON.parse(data ? data.textContent : '{}');
    this.rating = root.querySelector('[data-dcc-rating]');
    this.intro = root.querySelector('[data-dcc-intro]');
    this.after = root.querySelector('[data-dcc-after]');
    this.images = (this.product.images || []).filter(function (im) { return im && typeof im.src === 'string' && im.src; });
    if (!this.images.length && this.product.sample) this.images = [{ src: this.product.sample, alt: this.product.title || '' }];
    this.device = deviceId();
    var lastUpload = recall(UPLOAD_KEY);
    this.s = {
      file: null, photoUrl: null, uploadId: /^[a-z2-7]{24}$/.test(lastUpload || '') ? lastUpload : null,
      designs: [], selected: null, quota: null, view: 0,
      variant: (this.product.variants || [])[0] || null, busy: false, next: 0, needEmail: false, limitHit: false
    };
    this.build();
    this.render();
    if (!this.ready) this.refresh();
  }

  Customizer.prototype.build = function () {
    var self = this, p = this.product;
    this.art = el('img', { class: 'dcc-art', alt: p.title || 'Canvas preview' });
    this.badge = el('span', { class: 'dcc-badge', text: 'Sample' });
    this.progressText = el('p', { class: 'dcc-progress-text' });
    this.progress = el('div', { class: 'dcc-progress', 'aria-hidden': 'true' },
      [el('span', { class: 'dcc-spinner' }), this.progressText]);
    var frame = el('div', { class: 'dcc-frame' }, [this.art, this.badge, this.progress]);
    var shape = /^\d+:\d+$/.test(p.gallery_aspect || '') ? p.gallery_aspect : (p.aspect || '2:3');
    this.root.style.setProperty('--dcc-gallery-ratio', shape.replace(':', ' / '));
    if (p.aspect && shape !== p.aspect) this.root.classList.add('dcc-fit');
    this.thumbs = el('div', { class: 'dcc-thumbs' });

    this.fileInput = el('input', { type: 'file', accept: 'image/*', class: 'dcc-file', tabindex: '-1',
                                   'aria-hidden': 'true', onchange: function () { self.pick(this.files[0]); this.value = ''; } });
    this.thumb = el('img', { class: 'dcc-thumb', alt: '', hidden: true });
    // Start fetching the face check while the customer is still choosing the photo.
    var warmGate = function () { faceGate(self.api).catch(function () { /* checked again on pick */ }); };
    this.pickBtn = el('button', { type: 'button', class: 'dcc-btn is-secondary',
                                  onclick: function () { warmGate(); self.fileInput.click(); } }, ['Choose a photo']);
    this.drop = el('div', { class: 'dcc-drop' }, [
      this.thumb, el('div', { class: 'dcc-drop-text' }, [this.pickBtn,
        el('p', { class: 'dcc-hint', text: 'One child, face clearly visible, good light.' })]),
      this.fileInput]);
    ['dragenter', 'dragover'].forEach(function (ev) {
      self.drop.addEventListener(ev, function (e) { e.preventDefault(); self.drop.classList.add('is-over'); });
    });
    this.drop.addEventListener('dragenter', warmGate);
    this.facesBox = el('div', { class: 'dcc-faces', hidden: true });
    this.photoNote = el('span', {});
    this.anywayBtn = el('button', { type: 'button', class: 'dcc-link', hidden: true,
                                    onclick: function () { self.useAnyway(); } }, ['Use this photo anyway']);
    this.photoBox = el('p', { class: 'dcc-photo-note', role: 'status', 'aria-live': 'polite', hidden: true },
      [this.photoNote, ' ', this.anywayBtn]);
    ['dragleave', 'drop'].forEach(function (ev) {
      self.drop.addEventListener(ev, function () { self.drop.classList.remove('is-over'); });
    });
    this.drop.addEventListener('drop', function (e) {
      e.preventDefault();
      if (e.dataTransfer && e.dataTransfer.files[0]) self.pick(e.dataTransfer.files[0]);
    });
    var terms = el('a', { href: p.terms_url || '/pages/photo-terms', target: '_blank', rel: 'noopener',
                          text: 'photo terms' });
    var termsNote = el('p', { class: 'dcc-terms' }, ['By adding a photo, you agree to our ', terms, '.']);

    this.versions = el('div', { class: 'dcc-versions' });
    this.makeBtn = el('button', { type: 'button', class: 'dcc-btn', onclick: function () { self.create(); } });
    this.quota = el('p', { class: 'dcc-quota' });
    this.email = el('input', { type: 'email', class: 'dcc-input', placeholder: 'Your email', autocomplete: 'email',
                               'aria-label': 'Your email' });
    this.deals = el('input', { type: 'checkbox' });
    this.unlockBtn = el('button', { type: 'button', class: 'dcc-btn', onclick: function () { self.unlock(); } },
      ['Unlock']);
    this.unlockBox = el('div', { class: 'dcc-unlock', hidden: true }, [
      el('p', { class: 'dcc-unlock-title', text: 'Get more free previews' }),
      el('div', { class: 'dcc-row dcc-unlock-row' }, [this.email, this.unlockBtn]),
      el('label', { class: 'dcc-check' }, [this.deals, el('span', { text: 'Email me Dino Spirit deals.' })])]);
    this.email.addEventListener('keydown', function (e) { if (e.key === 'Enter') self.unlock(); });

    this.sizes = el('div', { class: 'dcc-sizes', role: 'group', 'aria-label': 'Size' });
    this.price = el('p', { class: 'dcc-price' });
    this.atc = el('button', { type: 'button', class: 'dcc-btn is-wide dcc-atc', onclick: function () { self.addToCart(); } });
    this.msg = el('p', { class: 'dcc-msg', role: 'status', 'aria-live': 'polite' });
    this.added = el('div', { class: 'dcc-added', hidden: true });

    var steps = this.ready ? [el('section', { class: 'dcc-step' }, [el('h2', { text: 'Choose a size' }), this.sizes])] : [
      el('section', { class: 'dcc-step' }, [el('h2', { text: "1. Add your child's photo" }), this.drop,
        this.facesBox, this.photoBox, termsNote]),
      el('section', { class: 'dcc-step' }, [el('h2', { text: '2. Your preview' }), this.versions,
        el('div', { class: 'dcc-row' }, [this.makeBtn, this.quota]), this.unlockBox]),
      el('section', { class: 'dcc-step' }, [el('h2', { text: '3. Choose a size' }), this.sizes])];
    var panel = el('div', { class: 'dcc-panel' }, [
      el('h1', { class: 'dcc-title', text: p.title || 'Personalized canvas' }),
      this.rating,
      this.price,
      this.intro].concat(steps, [this.atc, this.msg, this.added, this.after]));

    if (p.eta && p.eta.min && p.eta.max && this.after) {
      Array.prototype.forEach.call(this.after.querySelectorAll('[data-dcc-eta]'), function (n) {
        n.textContent = etaText(p.eta);
      });
    }

    this.root.classList.add('dcc');
    this.stage = el('div', { class: 'dcc-stage' }, [frame, this.thumbs]);
    this.root.appendChild(this.stage);
    this.root.appendChild(panel);
    this.buildSizes();
  };

  Customizer.prototype.buildSizes = function () {
    var self = this;
    (this.product.variants || []).forEach(function (v) {
      var b = el('button', { type: 'button', class: 'dcc-size', onclick: function () { self.s.variant = v; self.render(); } }, [
        el('span', { class: 'dcc-size-title', text: v.title }),
        v.label ? el('span', { class: 'dcc-size-label', text: v.label }) : null,
        el('span', { class: 'dcc-size-price', text: money(v.price) })]);
      b._variant = v;
      self.sizes.appendChild(b);
    });
  };

  Customizer.prototype.say = function (text, isError) {
    this.msg.textContent = text || '';
    this.msg.classList.toggle('is-error', !!isError);
  };

  Customizer.prototype.working = function (text) {
    this.progressText.textContent = text || '';
    this.progress.classList.toggle('is-on', !!text);
  };

  // New photo: find the faces in the browser, then use a square around the child's face (one face), let the
  // customer tap the child (several), or say so (none). Only that square is uploaded.
  Customizer.prototype.pick = function (file) {
    if (!file) return;
    if (!/^image\//.test(file.type || '')) return this.say(MESSAGES.bad_type, true);
    var self = this, s = this.s, ticket = this.ticket = (this.ticket || 0) + 1;
    s.uploadId = null;
    this.closeFaces();
    this.setPhoto(null);
    this.pending = null;
    this.say('');
    this.note(NOTES.checking);
    s.checking = true;
    this.render();
    var current = function () { return ticket === self.ticket; };
    loadImage(file).then(function (loaded) {
      var img = loaded.img;
      return within(faceGate(self.api), GATE_WAIT_MS)
        .then(function (gate) { return detectFaces(gate, img); })
        .then(function (faces) {
          if (!current()) return URL.revokeObjectURL(loaded.url);
          if (faces.length > 1) return self.showFaces(file, loaded, faces);
          URL.revokeObjectURL(loaded.url);
          if (!faces.length) return self.noFace(file, img);
          return cropToBlob(img, faceCrop(faces[0], img.naturalWidth, img.naturalHeight, false)).then(function (blob) {
            if (current()) self.usePhoto(blob, Math.max(faces[0].w, faces[0].h) < SMALL_FACE);
          });
        }, function () {  // the check could not run (slow network, old browser): the photo as it is
          URL.revokeObjectURL(loaded.url);
          if (current()) self.usePhoto(file, Math.min(img.naturalWidth, img.naturalHeight) < SMALL_PHOTO);
        });
    }).catch(function (err) {
      if (!current()) return;
      s.checking = false;
      self.note('');
      self.say(MESSAGES[(err && err.code) || 'bad_type'] || MESSAGES.bad_type, true);
      self.render();
    });
  };

  Customizer.prototype.note = function (text, warn, anyway) {
    this.photoNote.textContent = text || '';
    this.photoBox.hidden = !text;
    this.photoBox.classList.toggle('is-warn', !!warn);
    this.anywayBtn.hidden = !anyway;
  };

  Customizer.prototype.setPhoto = function (blob) {
    if (this.s.photoUrl) URL.revokeObjectURL(this.s.photoUrl);
    this.s.file = blob;
    this.s.photoUrl = blob ? URL.createObjectURL(blob) : null;
  };

  Customizer.prototype.usePhoto = function (blob, small) {
    this.s.checking = false;
    this.pending = null;
    this.setPhoto(blob);
    this.note(small ? NOTES.small : '', true);
    this.render();
  };

  Customizer.prototype.noFace = function (file, img) {
    this.s.checking = false;
    this.pending = { file: file, small: Math.min(img.naturalWidth, img.naturalHeight) < SMALL_PHOTO };
    this.note(NOTES.no_face, true, true);
    this.render();
  };

  Customizer.prototype.useAnyway = function () {
    if (this.pending) this.usePhoto(this.pending.file, this.pending.small);
  };

  Customizer.prototype.showFaces = function (file, loaded, faces) {
    var self = this, img = loaded.img, w = img.naturalWidth, h = img.naturalHeight;
    var frame = el('div', { class: 'dcc-faces-frame' }, [el('img', { src: loaded.url, alt: 'Your photo' })]);
    faces.forEach(function (f, i) {
      // A little bigger than the face itself, so small faces are easy to tap.
      var pad = Math.max(f.w, f.h) * 0.15;
      var x = Math.max(0, f.x - pad), y = Math.max(0, f.y - pad);
      var b = el('button', { type: 'button', class: 'dcc-face', 'aria-label': 'Face ' + (i + 1) + ' of ' + faces.length,
                             onclick: function () { self.chooseFace(img, f); } });
      b.style.left = (x / w * 100) + '%';
      b.style.top = (y / h * 100) + '%';
      b.style.width = (Math.min(w - x, f.w + 2 * pad) / w * 100) + '%';
      b.style.height = (Math.min(h - y, f.h + 2 * pad) / h * 100) + '%';
      frame.appendChild(b);
    });
    this.facesBox.textContent = '';
    this.facesBox.appendChild(frame);
    this.facesBox.hidden = false;
    this.facesUrl = loaded.url;
    this.s.checking = false;
    this.s.choosing = true;
    this.note(NOTES.pick.replace('{n}', faces.length));
    this.render();
    var first = frame.querySelector('.dcc-face');
    if (first) first.focus({ preventScroll: true });
  };

  Customizer.prototype.chooseFace = function (img, face) {
    var self = this, ticket = this.ticket;
    cropToBlob(img, faceCrop(face, img.naturalWidth, img.naturalHeight, true)).then(function (blob) {
      if (ticket !== self.ticket) return;
      self.closeFaces();
      self.usePhoto(blob, Math.max(face.w, face.h) < SMALL_FACE);
    }).catch(function () { self.say(MESSAGES.bad_type, true); });
  };

  Customizer.prototype.closeFaces = function () {
    this.s.choosing = false;
    this.facesBox.hidden = true;
    this.facesBox.textContent = '';
    if (this.facesUrl) URL.revokeObjectURL(this.facesUrl);
    this.facesUrl = null;
  };

  Customizer.prototype.selected = function () {
    var id = this.s.selected;
    return this.s.designs.filter(function (d) { return d.id === id; })[0] || null;
  };

  Customizer.prototype.safeDesign = function (d) {
    var base = this.api + '/img/';
    return d && typeof d.id === 'string' && typeof d.preview === 'string' && typeof d.thumb === 'string' &&
      d.preview.indexOf(base) === 0 && d.thumb.indexOf(base) === 0;
  };

  Customizer.prototype.refresh = function () {
    var self = this;
    var url = this.api + '/designs?store=' + encodeURIComponent(this.product.store) + '&device=' + this.device;
    return request(url).then(function (body) {
      if (body._status !== 200) return;
      self.s.quota = body.quota || self.s.quota;
      self.s.needEmail = !!(self.s.quota && self.s.quota.email_required);
      var mine = (body.designs || []).filter(function (d) {
        return d.template === self.product.template && self.safeDesign(d);
      });
      if (mine.length) {
        var first = !self.s.designs.length;
        self.s.designs = mine;
        if (!self.selected()) self.s.selected = mine[0].id;
        if (first) self.s.view = 'design';
      }
      self.render();
    }).catch(function () { /* offline: the first action will say so */ });
  };

  Customizer.prototype.upload = function () {
    var self = this, s = this.s;
    this.working('Uploading your photo...');
    return shrink(s.file, 2048).then(function (blob) {
      var form = new FormData();
      form.append('store', self.product.store);
      form.append('device', self.device);
      form.append('file', blob, 'photo.jpg');
      return request(self.api + '/upload', { method: 'POST', body: form });
    }).then(function (body) {
      if (body._status !== 200) throw fail(body.error);
      s.uploadId = body.upload_id;
      remember(UPLOAD_KEY, s.uploadId);
    });
  };

  Customizer.prototype.generate = function () {
    var self = this, s = this.s;
    this.working('Painting your little one...');
    return request(this.api + '/generate', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ store: this.product.store, template: this.product.template, device: this.device,
                             upload_id: s.uploadId, variant: VERSIONS[s.next % VERSIONS.length] })
    }).then(function (body) {
      if (body.quota) s.quota = body.quota;
      if (body.error === 'upload_not_found') {
        s.uploadId = null;
        remember(UPLOAD_KEY, null);
        throw fail('source_missing');
      }
      if (body._status !== 200) throw fail(body.error);
      s.next += 1;
      return body.job_id;
    });
  };

  Customizer.prototype.poll = function (jobId) {
    var self = this, started = Date.now();
    var url = this.api + '/jobs/' + encodeURIComponent(jobId) + '?device=' + this.device;
    return new Promise(function (resolve, reject) {
      (function tick() {
        request(url).then(function (body) {
          if (body.status === 'done' && body.design) return resolve(body.design);
          if (body.status === 'failed' || body._status !== 200) return reject(fail(body.error));
          if (Date.now() - started > TIMEOUT_MS) return reject(fail('timeout'));
          self.working(body.status === 'queued' && body.ahead
            ? 'You are in line (' + body.ahead + ' ahead)...' : 'Painting your little one...');
          setTimeout(tick, POLL_MS);
        }, function () {
          if (Date.now() - started > TIMEOUT_MS) return reject(fail('network'));
          setTimeout(tick, POLL_MS * 2);
        });
      })();
    });
  };

  Customizer.prototype.create = function () {
    var self = this, s = this.s;
    if (s.busy) return;
    if (!s.file && !s.uploadId) { this.say(MESSAGES.no_photo, true); this.pickBtn.focus(); return; }
    s.busy = true;
    this.say('');
    this.render();
    // On phones the picture sits above the steps: bring it into view to show progress and the result.
    if (window.matchMedia && window.matchMedia('(max-width: 860px)').matches) {
      this.stage.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    (s.uploadId ? Promise.resolve() : this.upload())
      .then(function () { return self.generate(); })
      .then(function (jobId) { return self.poll(jobId); })
      .then(function (design) {
        if (!self.safeDesign(design)) throw fail('generation_failed');
        s.designs = [design].concat(s.designs.filter(function (d) { return d.id !== design.id; }));
        s.selected = design.id;
        s.view = 'design';
      })
      .catch(function (err) { self.problem(err); })
      .then(function () {
        s.busy = false;
        self.working(null);
        self.render();
        return self.refresh();
      });
  };

  // All free and email previews used, or a daily limit hit: no new preview, but the customer can still order.
  Customizer.prototype.outOfPreviews = function () {
    var q = this.s.quota;
    return !this.ready && (this.s.limitHit || (!!q && q.left <= 0 && !this.s.needEmail));
  };

  Customizer.prototype.problem = function (err) {
    var code = (err && err.code) || 'network';
    if (code === 'device_limit' || code === 'ip_limit') {  // step 2 now says how to order without a preview
      this.s.limitHit = true;
      this.say('');
      return;
    }
    if (code === 'email_required') {
      this.s.needEmail = true;
      this.say('');
      var email = this.email;
      setTimeout(function () { email.focus(); }, 0);
      return;
    }
    this.say(MESSAGES[code] || MESSAGES.generation_failed, true);
  };

  Customizer.prototype.unlock = function () {
    var self = this, email = this.email.value.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return this.say(MESSAGES.bad_email, true);
    this.unlockBtn.disabled = true;
    request(this.api + '/unlock', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ store: this.product.store, device: this.device, email: email, consent: this.deals.checked })
    }).then(function (body) {
      if (body._status !== 200) throw fail(body.error);
      self.s.quota = body.quota;
      self.s.needEmail = false;
      self.say('');
      self.render();
      self.create();
    }).catch(function (err) { self.problem(err); })
      .then(function () { self.unlockBtn.disabled = false; });
  };

  // Same flow as the Dino Spirit theme (assets/ds-bundle.js): add with the cart sections, then fire the
  // theme events so the cart drawer opens and the ladder refreshes.
  Customizer.prototype.addToCart = function () {
    var self = this, s = this.s, d = this.selected(), v = s.variant;
    if (!v || s.busy) return;
    var later = !d && this.outOfPreviews();
    if (!d && !this.ready && !later) { this.say(MESSAGES.no_design, true); return; }
    if (later && !s.file && !s.uploadId) { this.say(MESSAGES.no_photo, true); this.pickBtn.focus(); return; }
    if (later && !s.uploadId) {  // the team paints from the uploaded photo: upload it first
      s.busy = true;
      this.say('');
      this.render();
      this.upload().then(function () {
        s.busy = false;
        self.working(null);
        self.addToCart();
      }, function (err) {
        s.busy = false;
        self.working(null);
        self.render();
        self.problem(err);
      });
      return;
    }
    var item = this.ready ? { id: v.id, quantity: 1 }
      : d ? { id: v.id, quantity: 1, properties: { _design_id: d.id, _preview: d.thumb } }
      : { id: v.id, quantity: 1, properties: { 'Your canvas': LATER.line, _upload_id: s.uploadId,
                                               _template: this.product.template } };
    if (this.demo) return this.showAdded(item, d, v);
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    var sections = [];
    document.dispatchEvent(new CustomEvent('cart:push:section.id', { detail: { sections: sections } }));
    var sectionIds = sections.filter(function (id, i) { return sections.indexOf(id) === i; }).slice(0, 5);
    var added;
    this.atc.disabled = true;
    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [item], sections: sectionIds, sections_url: location.pathname })
    }).then(function (r) {
      return r.json().then(function (body) {
        if (!r.ok) throw fail('cart');
        added = body;
        return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
      });
    }).then(function (r) {
      if (!r.ok) throw fail('cart');
      return r.json();
    }).then(function (cart) {
      var have = added.sections || {};
      if (!sectionIds.length || sectionIds.every(function (id) { return have[id]; })) return [cart, have];
      var url = new URL(location.href);
      url.searchParams.set('sections', sectionIds.join(','));
      return fetch(url).then(function (r) {
        if (!r.ok) throw fail('cart');
        return r.json().then(function (rendered) { return [cart, rendered]; });
      });
    }).then(function (pair) {
      var cart = pair[0];
      cart.sections = pair[1];
      self.root.dispatchEvent(new CustomEvent('variant:add', { bubbles: true,
        detail: { items: added.items || [added], cart: cart } }));
      document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true,
        detail: { baseEvent: 'variant:add', cart: cart } }));
      self.say('');
    }).catch(function (err) {
      self.problem(err);
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }).then(function () { self.atc.disabled = false; });
  };

  Customizer.prototype.showAdded = function (item, d, v) {
    var self = this;
    var props = el('pre', { class: 'dcc-props', text: JSON.stringify(item.properties, null, 2) });
    var close = el('button', { type: 'button', class: 'dcc-btn is-secondary',
                               onclick: function () { self.added.hidden = true; } }, ['Close']);
    this.added.textContent = '';
    [el('p', { class: 'dcc-added-title', text: 'Added to cart (demo)' }),
     el('div', { class: 'dcc-row' }, [el('img', { class: 'dcc-thumb', src: d ? d.thumb : this.s.photoUrl || '', alt: '' }),
       el('div', {}, [el('p', { class: 'dcc-added-line', text: (this.product.title || '') + ' - ' + v.title }),
                      el('p', { class: 'dcc-added-line', text: money(v.price) })])]),
     el('p', { class: 'dcc-hint', text: 'Hidden order details sent with this item:' }), props, close
    ].forEach(function (n) { self.added.appendChild(n); });
    this.added.hidden = false;
    close.focus();
  };

  Customizer.prototype.renderGallery = function (d) {
    var self = this, s = this.s;
    var showDesign = !!d && s.view === 'design';
    var image = showDesign ? null : (this.images[s.view] || this.images[0] || null);
    this.art.src = showDesign ? d.preview : (image ? image.src : '');
    this.art.classList.toggle('is-design', showDesign);
    if (image && image.alt) this.art.alt = image.alt;
    this.badge.hidden = this.ready || (!showDesign && s.view !== 0);
    this.badge.textContent = showDesign ? 'Your preview' : 'Sample';

    this.thumbs.textContent = '';
    var add = function (src, label, pressed, onclick, design) {
      self.thumbs.appendChild(el('button', { type: 'button', class: design ? 'dcc-gthumb is-design' : 'dcc-gthumb',
                                             'aria-label': label, 'aria-pressed': String(pressed), onclick: onclick },
        [el('img', { src: src, alt: '' })]));
    };
    if (d) add(d.thumb, 'Your preview', showDesign, function () { s.view = 'design'; self.render(); }, true);
    this.images.forEach(function (im, i) {
      add(typeof im.thumb === 'string' && im.thumb ? im.thumb : im.src, im.alt || 'Photo ' + (i + 1),
          !showDesign && s.view === i, function () { s.view = i; self.render(); });
    });
    this.thumbs.hidden = this.thumbs.children.length < 2;
  };

  Customizer.prototype.render = function () {
    var s = this.s, d = this.selected(), self = this;
    this.renderGallery(d);

    this.thumb.hidden = !s.photoUrl;
    if (s.photoUrl) this.thumb.src = s.photoUrl;
    this.pickBtn.textContent = s.photoUrl || s.choosing || this.pending ? 'Change photo' : 'Choose a photo';
    this.pickBtn.disabled = s.busy;

    this.versions.textContent = '';
    this.versions.hidden = s.designs.length < 2;
    s.designs.forEach(function (design, i) {
      self.versions.appendChild(el('button', {
        type: 'button', class: 'dcc-version', 'aria-pressed': String(design.id === s.selected),
        'aria-label': 'Version ' + (s.designs.length - i),
        onclick: function () { s.selected = design.id; s.view = 'design'; self.render(); }
      }, [el('img', { src: design.thumb, alt: '' })]));
    });

    var q = s.quota, out = this.outOfPreviews(), later = out && !d;
    this.makeBtn.textContent = s.designs.length ? 'Try another version' : 'Create my free preview';
    this.makeBtn.className = 'dcc-btn' + (s.designs.length ? ' is-secondary' : '');
    this.makeBtn.disabled = s.busy || s.needEmail || s.checking || s.choosing || !!this.pending;
    this.makeBtn.hidden = out;
    this.quota.textContent = later ? LATER.note : !q || s.needEmail ? '' : out ? 'No free previews left'
      : q.left === 1 ? '1 free preview left' : q.left + ' free previews left';
    this.unlockBox.hidden = !s.needEmail;

    Array.prototype.forEach.call(this.sizes.children, function (b) {
      b.setAttribute('aria-pressed', String(b._variant === s.variant));
    });
    // Like the theme's product price: the struck regular price first, then the sale price in red.
    this.price.textContent = '';
    if (s.variant) {
      var onSale = Number(s.variant.compare) > Number(s.variant.price);
      if (onSale) {
        this.price.appendChild(el('s', { class: 'dcc-price-was', 'aria-label': 'Regular price ' + money(s.variant.compare),
                                         text: money(s.variant.compare) }));
      }
      this.price.appendChild(el('span', { class: onSale ? 'dcc-price-now is-sale' : 'dcc-price-now',
                                          'aria-label': (onSale ? 'Sale price ' : 'Price ') + money(s.variant.price),
                                          text: money(s.variant.price) }));
    }
    if (this.after) {
      Array.prototype.forEach.call(this.after.querySelectorAll('[data-dcc-preview-note]'), function (n) { n.hidden = later; });
    }
    var waiting = !d && !this.ready && !(later && (s.file || s.uploadId));
    this.atc.disabled = !s.variant || s.busy;
    this.atc.classList.toggle('is-waiting', waiting);
    this.atc.textContent = !waiting ? 'ADD TO CART'
      : later ? 'Choose a photo to add to cart' : 'Create your preview to add to cart';
  };

  function mountAll() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-canvas-customizer]'), function (root) {
      if (!root._dcc) root._dcc = new Customizer(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mountAll);
  else mountAll();
})();
